import { Trans } from "@lingui/macro";
import { ContentContainer, Form, Switch, Typography } from "@ster/ster-toolkit";
import { Form as AntForm } from "antd";
import { useEffect, useMemo } from "react";

import { SubOrderRequest } from "../../api";
import { BudgetBonusFormSchema } from "../campaignCreate/subOrder/models";
import BudgetBonusFormSection from "../campaignCreate/subOrder/partials/BudgetBonusFormSection";
import { useCampaign } from "../providers/CampaignProvider";
import { OnChangeSubOrder } from "./models";
import styles from "./SubOrdersDiscount.module.less";

type SubOrdersDiscountFormSchema = BudgetBonusFormSchema & {
  bonusOnOrderRequest: boolean;
};

const SubOrdersDiscount = ({
  subOrders,
  onChangeSubOrder,
}: {
  subOrders: Partial<SubOrderRequest>[];
  onChangeSubOrder: OnChangeSubOrder;
}) => {
  const [form] = AntForm.useForm<SubOrdersDiscountFormSchema>();
  const { orderRequest, updateOrderRequest } = useCampaign();

  // Regional: only fixed costs sub orders should be available AND have budget and bonus - ignore other sub orders
  const allSubOrdersAreFixedCosts = subOrders.every(
    (subOrder) => subOrder.packageChoice === "FixedCosts"
  );

  const totals = useMemo(
    () =>
      subOrders.reduce(
        (acc, { bonus, budget }) => {
          acc.totalBonus += bonus ?? 0;
          acc.totalBudget += budget ?? 0;
          return acc;
        },
        {
          totalBonus: 0,
          totalBudget: 0,
        }
      ),
    [subOrders]
  );

  const { totalBudget, totalBonus } = totals;

  // Initialize
  useEffect(() => {
    form.setFieldsValue({
      budget: totalBudget,
      bonus: totalBonus,
      bonusOnOrderRequest:
        orderRequest?.bonusConfiguration?.bonusOnOrderRequest ?? false,
    });
  }, [
    form,
    totalBudget,
    totalBonus,
    orderRequest?.bonusConfiguration?.bonusOnOrderRequest,
  ]);

  const applyNewBonus = (newBonusTotal: number) => {
    const updateData = subOrders.map(({ budget, id }) => {
      const budgetProportion = (budget ?? 0) / totalBudget;
      const newBonus = Math.round(newBonusTotal * budgetProportion);

      return {
        subOrderId: id,
        budget,
        budgetProportion,
        newBonus,
      };
    });

    // Calculate the total of the rounded newBonus values
    const roundedTotal = updateData.reduce(
      (sum, subOrderData) => sum + subOrderData.newBonus,
      0
    );

    // Adjust the last newBonus value to ensure the total matches newBonusTotal
    const adjustment = newBonusTotal - roundedTotal;
    if (updateData.length > 0) {
      updateData[updateData.length - 1].newBonus += adjustment;
    }

    onChangeSubOrder(
      updateData.map((data) => ({
        bonus: data.newBonus,
        id: data.subOrderId,
      }))
    );
  };

  // Handle changes
  const handleValuesChange = (
    changedValues: Partial<SubOrdersDiscountFormSchema>,
    _allValues: SubOrdersDiscountFormSchema
  ) => {
    if (changedValues.bonusOnOrderRequest !== undefined) {
      // update order request directly
      updateOrderRequest(() => ({
        bonusConfiguration: {
          bonusOnOrderRequest: changedValues.bonusOnOrderRequest,
        },
      }));
    }

    if (changedValues.bonus !== undefined) {
      // apply new bonus
      applyNewBonus(changedValues.bonus);
    }
  };

  const canRender = allSubOrdersAreFixedCosts;

  // Rendering
  if (!canRender) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Typography.Title level={3} className={styles.title}>
        <Trans>Bonus hoofdorder</Trans>
      </Typography.Title>
      <ContentContainer className={styles.content}>
        <Form<SubOrdersDiscountFormSchema>
          form={form}
          onValuesChange={handleValuesChange}
          layout="horizontal"
          className="fixedWidthLabels"
        >
          <Form.Item<SubOrdersDiscountFormSchema>
            label={<Trans>Bonus op hoofdorder</Trans>}
          >
            <Form.Item<SubOrdersDiscountFormSchema>
              label={
                <Form.Item name="bonusOnOrderRequest" noStyle>
                  <Switch />
                </Form.Item>
              }
              wrapperCol={{ span: 14 }}
              className="inlineWidthLabels"
            >
              <BudgetBonusFormSection
                form={form}
                disabled={
                  !orderRequest?.bonusConfiguration?.bonusOnOrderRequest
                }
                budgetEditable={false}
                bonusEditable
                rules={{
                  bonus: {
                    maximum: totalBudget,
                    minimum: 0,
                  },
                  budget: {
                    minimum: 0,
                  },
                }}
              />
            </Form.Item>
          </Form.Item>
        </Form>
      </ContentContainer>
    </div>
  );
};

export default SubOrdersDiscount;
